/**
 * Documents (Tài liệu) — Office/PDF viewing with completion gates
 */

import type { ContentConfig, DocumentsCompletionConfig } from '@ai-content-hub/content-core';
import type { PageElementData, PageBlock } from '@ai-content-hub/content-elements';
import { defaultPageData } from '@ai-content-hub/content-elements';

export interface DocumentsData {
    title?: string;
    description?: string;
    pageContent: PageElementData;
}

export interface DocumentsContent {
    id: string;
    type: 'documents';
    version: string;
    data: DocumentsData;
    config?: DocumentsConfig;
}

export type DocumentsConfig = ContentConfig;

export const defaultDocumentsI18n = {
    en: {
        contentTab: 'Contents',
        configTab: 'Configuration',
        markDone: 'Mark done',
        requireFullScrollLabel: 'Require scroll to end before marking done',
        minViewSecondsLabel: 'Minimum viewing time (seconds, 0 = off)',
        minViewSecondsHint: 'Example: 180 for three minutes while this tab stays visible.',
        dwellHint: 'Stay on this activity for at least {{seconds}}s (visible tab).',
        scrollHint: 'Scroll to the bottom of the document to continue.',
        gateHint: 'Complete the requirements above to mark done.',
        completed: 'Completed',
        titleLabel: 'Title',
        titlePlaceholder: 'Documents title',
        descLabel: 'Instructions (optional)',
        descPlaceholder: 'Short instructions',
    },
    vi: {
        contentTab: 'Nội dung',
        configTab: 'Cấu hình',
        markDone: 'Hoàn thành',
        requireFullScrollLabel: 'Phải cuộn hết tài liệu mới được đánh dấu hoàn thành',
        minViewSecondsLabel: 'Thời gian xem tối thiểu (giây, 0 = tắt)',
        minViewSecondsHint: 'Ví dụ: 180 cho ba phút khi tab này vẫn hiển thị.',
        dwellHint: 'Ở lại ít nhất {{seconds}} giây (tab đang hiển thị).',
        scrollHint: 'Cuộn xuống cuối tài liệu để tiếp tục.',
        gateHint: 'Hoàn tất các yêu cầu phía trên để đánh dấu hoàn thành.',
        completed: 'Đã hoàn thành',
        titleLabel: 'Tiêu đề',
        titlePlaceholder: 'Tiêu đề tài liệu',
        descLabel: 'Hướng dẫn (tuỳ chọn)',
        descPlaceholder: 'Hướng dẫn ngắn gọn',
    },
} as const;

export const defaultDocumentsConfig: DocumentsConfig = {
    documents: {
        requireFullScroll: false,
        minimumViewSeconds: 0,
    },
    behavior: {
        allowRetry: true,
        maxAttempts: 0,
        shuffleChoices: false,
        showCorrectAnswer: false,
        showExplanation: false,
        showHint: false,
        instantFeedback: false,
        examMode: false,
        autoAdvance: false,
    },
    scoring: {
        max: 1,
        partialCreditEnabled: false,
        penaltyPerWrongAnswer: 0,
        passingScore: 1,
    },
    i18n: {
        defaultLanguage: 'en',
        languages: defaultDocumentsI18n,
    },
};

export const defaultDocumentsData: DocumentsData = {
    title: '',
    description: '',
    pageContent: { ...defaultPageData },
};

export const documentsSchema = {
    type: 'documents' as const,
    title: 'Documents',
    description: 'Tài liệu — viewing Office/PDF documents with configurable completion rules',
    version: '1.0.0',
    category: 'layout' as const,
    icon: 'FileStack',
    tags: ['documents', 'office', 'pdf', 'viewer'],
    defaultData: defaultDocumentsData,
    defaultConfig: defaultDocumentsConfig,
};

export function listPageBlocks(page: PageElementData | undefined): PageBlock[] {
    if (!page?.sections?.length) return [];
    const out: PageBlock[] = [];
    for (let i = 0; i < page.sections.length; i += 1) {
        const sec = page.sections[i];
        const blocks = sec.blocks ?? [];
        for (let j = 0; j < blocks.length; j += 1) {
            out.push(blocks[j]);
        }
    }
    return out;
}

export function hasConfiguredOfficeDocument(data: DocumentsData): boolean {
    const blocks = listPageBlocks(data.pageContent);
    for (let i = 0; i < blocks.length; i += 1) {
        const c = blocks[i]?.content;
        if (c && c.type === 'office-document' && (c.src ?? '').trim()) {
            return true;
        }
    }
    return false;
}

export type DocumentsAnswer = {
    completed: boolean;
    viewedSeconds?: number;
    scrolledToEnd?: boolean;
};

export function normalizeDocumentsCompletion(
    raw: DocumentsCompletionConfig | undefined
): Required<Pick<DocumentsCompletionConfig, 'requireFullScroll' | 'minimumViewSeconds'>> {
    return {
        requireFullScroll: raw?.requireFullScroll ?? false,
        minimumViewSeconds: Math.max(0, raw?.minimumViewSeconds ?? 0),
    };
}
